import * as React from 'react';
import { useEffect } from 'react';
import { graphql } from 'react-apollo';
import Media from 'react-media';

import gql from 'graphql-tag';
import { compose } from 'recompose';

import connectToStores from 'js/lib/connectToStores';
import { useRetracked } from 'js/lib/retracked';
import type UserAgentInfo from 'js/lib/useragent';
import { LONG_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Col, Container, Row } from '@coursera/coursera-ui';

import CourseAccomplishmentsDataProvider from 'bundles/accomplishments/components/course-certificates/CourseAccomplishmentsDataProvider';
import CourseCertificate from 'bundles/accomplishments/components/course-certificates/CourseCertificate';
import type { XdpDataQuery_XdpV1Resource_slug_elements_xdpMetadata_XdpV1_cdpMetadataMember_cdpMetadata as CDPMetadata } from 'bundles/accomplishments/components/course-certificates/__generated__/XdpDataQuery';
import CertificateSharing from 'bundles/accomplishments/components/shared/CertificateSharing';
import CompletionOverview from 'bundles/accomplishments/components/shared/CompletionOverview';
import PageHeader from 'bundles/accomplishments/components/shared/PageHeader';
import ProductOverviewSnippet from 'bundles/accomplishments/components/shared/ProductOverviewSnippet';
import constants from 'bundles/accomplishments/constants';
import { formatPartnerNames } from 'bundles/accomplishments/utils/formatting';
import { getCourseCertProductUtm } from 'bundles/accomplishments/utils/utils';
import LearningObjectivesAndSkills from 'bundles/enterprise-legacy-xdp/components/LearningObjectivesAndSkills';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';
import { useGetBlockCertsInFreeTrialData } from 'bundles/subscriptions-common/utils/blockCertInFreeTrialUtils';

import _t from 'i18n!nls/accomplishments';

type PropsFromCaller = {
  courseName: string;
  userName: string;
  partnerNames: string[];
  userPhoto?: string;
  courseGrade?: string;
  courseLink: string;
  includeGrade?: boolean;
  certificateLink: string;
  courseCompletionDate: number;
  certificateImageUrl?: string;
  slug: string;
  isCertificateOwner?: boolean;
};

type PropsToComponent = PropsFromCaller & {
  cdpMetadata: CDPMetadata;
  userAgent: UserAgentInfo;
};

const CourseAccomplishmentPageContent = ({
  slug,
  courseName,
  userName,
  partnerNames,
  userPhoto,
  courseGrade,
  courseLink,
  includeGrade,
  certificateLink,
  courseCompletionDate,
  certificateImageUrl,
  isCertificateOwner,
  cdpMetadata,
  userAgent,
}: PropsToComponent) => {
  const { id, skills, cmlLearningObjectives, ratings, partners, courseTypeMetadata } = cdpMetadata || {};
  const utmProductParam = getCourseCertProductUtm(courseTypeMetadata);

  const blockCertsInFreeTrialData = useGetBlockCertsInFreeTrialData(isCertificateOwner ? { courseId: id } : {});
  const track = useRetracked();

  useEffect(() => {
    if (isCertificateOwner && blockCertsInFreeTrialData?.subscriptionId) {
      track({
        trackingData: {},
        trackingName: 'cert_without_payment',
        action: 'view',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockCertsInFreeTrialData?.subscriptionId]);

  return (
    <Container>
      <PageHeader includeGrade={includeGrade} name={courseName} courseTypeMetadata={courseTypeMetadata} />
      <CourseAccomplishmentsDataProvider slug={slug}>
        {({ enrollmentCount, duration }) => (
          <Media
            query={{ maxWidth: constants.MD_SCREEN_PX_BREAKPOINT - 1 }}
            defaultMatches={userAgent && userAgent.isMobileBrowser}
          >
            {(matches) => {
              if (matches) {
                // Mobile layout
                return (
                  <Row>
                    <Col md="6" rootClassName="left-col">
                      <CourseCertificate
                        userName={userName}
                        courseName={courseName}
                        universityNames={formatPartnerNames(partnerNames)}
                        certificateLink={certificateLink}
                        certificateImageUrl={certificateImageUrl}
                        isCertificateOwner={isCertificateOwner}
                      />
                      {cdpMetadata && (
                        <ProductOverviewSnippet
                          id={id}
                          ratings={ratings}
                          partners={partners}
                          name={courseName}
                          productLink={`/learn/${slug}`}
                          isCertificateOwner={isCertificateOwner}
                          enrollmentCount={enrollmentCount}
                        />
                      )}
                      <LearningObjectivesAndSkills skills={skills} cmlLearningObjectives={cmlLearningObjectives} />
                    </Col>
                    <Col md="6">
                      <CompletionOverview
                        userName={userName}
                        userPhoto={userPhoto}
                        name={courseName}
                        grade={courseGrade}
                        includeGrade={includeGrade}
                        productLink={courseLink}
                        completionDate={formatDateTimeDisplay(courseCompletionDate, LONG_DATE_ONLY_DISPLAY)}
                        totalDuration={duration}
                      />
                      <CertificateSharing
                        title={_t('Share this Certificate')}
                        productName={courseName}
                        partnerName={formatPartnerNames(partnerNames)}
                        certificatePdfLink={certificateLink}
                        utmProductParam={utmProductParam}
                        isCertificateOwner={isCertificateOwner}
                      />
                    </Col>
                  </Row>
                );
              } else {
                // Desktop
                return (
                  <Row>
                    <Col md="6" rootClassName="left-col">
                      <CompletionOverview
                        userName={userName}
                        userPhoto={userPhoto}
                        name={courseName}
                        grade={courseGrade}
                        includeGrade={includeGrade}
                        productLink={courseLink}
                        completionDate={formatDateTimeDisplay(courseCompletionDate, LONG_DATE_ONLY_DISPLAY)}
                        totalDuration={duration}
                      />
                      {cdpMetadata && (
                        <ProductOverviewSnippet
                          id={id}
                          ratings={ratings}
                          partners={partners}
                          name={courseName}
                          productLink={`/learn/${slug}`}
                          isCertificateOwner={isCertificateOwner}
                          enrollmentCount={enrollmentCount}
                        />
                      )}
                      <LearningObjectivesAndSkills skills={skills} cmlLearningObjectives={cmlLearningObjectives} />
                    </Col>
                    <Col md="6">
                      <CourseCertificate
                        userName={userName}
                        courseName={courseName}
                        universityNames={formatPartnerNames(partnerNames)}
                        certificateLink={certificateLink}
                        certificateImageUrl={certificateImageUrl}
                        isCertificateOwner={isCertificateOwner}
                      />
                      <CertificateSharing
                        title={_t('Share this Certificate')}
                        productName={courseName}
                        partnerName={formatPartnerNames(partnerNames)}
                        certificatePdfLink={certificateLink}
                        // @ts-expect-error ts-migrate(2551) FIXME: Property 'imageUtmContentParam' does not exist on ... Remove this comment to see the full error message
                        utmContentParam={constants.imageUtmContentParam}
                        utmProductParam={utmProductParam}
                        isCertificateOwner={isCertificateOwner}
                      />
                    </Col>
                  </Row>
                );
              }
            }}
          </Media>
        )}
      </CourseAccomplishmentsDataProvider>
    </Container>
  );
};

const XdpDataQuery = gql`
  query XdpDataQuery($slug: String!) {
    XdpV1Resource {
      slug(productType: "COURSE", slug: $slug) {
        elements {
          name
          id
          slug
          xdpMetadata {
            __typename
            ... on XdpV1_cdpMetadataMember {
              cdpMetadata {
                id
                slug
                cmlLearningObjectives {
                  ... on XdpV1_cmlMember {
                    cml {
                      value
                      dtdId
                    }
                  }
                }
                skills
                ratings {
                  averageFiveStarRating
                  ratingCount
                  commentCount
                }
                partners {
                  id
                  name
                  squareLogo
                }
                courseTypeMetadata {
                  __typename
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default compose<PropsToComponent, PropsFromCaller>(
  connectToStores<PropsToComponent, PropsFromCaller>([ApplicationStoreClass], (ApplicationStore, props) => ({
    ...props,
    userAgent: ApplicationStore.getUserAgent(),
  })),
  graphql(XdpDataQuery, {
    props: ({ data: { XdpV1Resource } }: any) => {
      return {
        cdpMetadata: XdpV1Resource?.slug?.elements?.[0]?.xdpMetadata?.cdpMetadata,
      };
    },
  })
)(CourseAccomplishmentPageContent);
